import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NAV_TARGETS, DART_GAME, ANIMATION_TIMINGS } from "../constants";
import { computeTrajectoryPoints, calculateVelocity } from "../utils/physics";
import { checkTargetCollision } from "../utils/collision";
import Target from "./DartGame/Target";
import Dart from "./DartGame/Dart";
import ToastNotifications from "./DartGame/ToastNotifications";

const INITIAL_ROTATION = -45;

export default function DartGame() {
  const containerRef = useRef(null);
  const targetRefs = useRef([]);
  const anchorRef = useRef({ x: 0, y: 0 });
  const hitRef = useRef(false);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(null);
  const velocityRef = useRef({ vx: 0, vy: 0 });
  const shotActiveRef = useRef(false);
  const flightStartRef = useRef(null);

  const [bounds, setBounds] = useState({ width: 0, height: 0 });
  const [dartPos, setDartPos] = useState({ x: 0, y: 0 });
  const [dartRotation, setDartRotation] = useState(INITIAL_ROTATION);
  const [isDragging, setIsDragging] = useState(false);
  const [aimVector, setAimVector] = useState(null);
  const [shotActive, setShotActive] = useState(false);
  const [hitTarget, setHitTarget] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [trajectoryPoints, setTrajectoryPoints] = useState([]);

  const posRef = useRef({ x: 0, y: 0 });
  const setDartPosition = useCallback((next) => {
    posRef.current = next;
    setDartPos(next);
  }, []);

  const floorY = useCallback(
    () => Math.max(DART_GAME.DART_SIZE + 8, bounds.height - DART_GAME.DART_SIZE - 30),
    [bounds.height]
  );

  const resetDart = useCallback(() => {
    if (!bounds.width || !bounds.height) return;
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    shotActiveRef.current = false;
    setShotActive(false);
    hitRef.current = false;
    velocityRef.current = { vx: 0, vy: 0 };
    lastTimeRef.current = null;
    flightStartRef.current = null;
    setAimVector(null);
    setTrajectoryPoints([]);
    setDartRotation(INITIAL_ROTATION);
    setDartPosition({
      x: bounds.width / 2,
      y: floorY(),
    });
  }, [bounds.height, bounds.width, floorY, setDartPosition]);

  const handleHit = useCallback(
    (target) => {
      if (!target || hitRef.current) return;
      hitRef.current = true;
      shotActiveRef.current = false;
      setShotActive(false);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      setHitTarget(target.id);
      setToasts((prev) => [
        ...prev,
        { id: `${target.id}-${Date.now()}`, label: target.label },
      ]);

      const section = document.getElementById(target.id);
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      setTimeout(() => setHitTarget(null), ANIMATION_TIMINGS.TARGET_HIT_DURATION);
      setTimeout(() => resetDart(), ANIMATION_TIMINGS.RESET_DELAY);
      setTimeout(
        () => setToasts((prev) => prev.slice(1)),
        ANIMATION_TIMINGS.TOAST_REMOVE_DELAY
      );
    },
    [resetDart]
  );

  const checkCollision = useCallback(
    (x, y) => {
      const collision = checkTargetCollision(x, y, targetRefs.current, containerRef.current);
      if (collision) {
        handleHit(NAV_TARGETS[collision.index]);
      }
    },
    [handleHit]
  );

  const step = useCallback(
    (timestamp) => {
      if (!shotActiveRef.current) return;
      
      if (!lastTimeRef.current) {
        lastTimeRef.current = timestamp;
        flightStartRef.current = timestamp;
        animationRef.current = requestAnimationFrame(step);
        return;
      }

      const delta = (timestamp - lastTimeRef.current) / 1000;
      lastTimeRef.current = timestamp;

      const flightTime = (timestamp - flightStartRef.current) / 1000;
      if (flightTime > DART_GAME.MAX_FLIGHT_TIME) {
        shotActiveRef.current = false;
        setShotActive(false);
        resetDart();
        return;
      }

      let nextPosition = { ...posRef.current };
      nextPosition.x += velocityRef.current.vx * delta;
      nextPosition.y += velocityRef.current.vy * delta;
      velocityRef.current.vy += DART_GAME.GRAVITY * delta;

      const angle = Math.atan2(velocityRef.current.vy, velocityRef.current.vx);
      setDartRotation((angle * 180) / Math.PI);

      const stopShot = () => {
        shotActiveRef.current = false;
        setShotActive(false);
        setTimeout(() => resetDart(), ANIMATION_TIMINGS.COLLISION_RESET_DELAY);
      };

      if (nextPosition.x < DART_GAME.DART_SIZE || nextPosition.x > bounds.width - DART_GAME.DART_SIZE) {
        stopShot();
        return;
      }

      if (nextPosition.y < DART_GAME.DART_SIZE) {
        stopShot();
        return;
      }

      const ground = floorY();
      if (nextPosition.y >= ground) {
        nextPosition.y = ground;
        stopShot();
        setDartPosition(nextPosition);
        return;
      }

      setDartPosition(nextPosition);
      checkCollision(nextPosition.x, nextPosition.y);

      if (shotActiveRef.current) {
        animationRef.current = requestAnimationFrame(step);
      }
    },
    [bounds.width, checkCollision, floorY, resetDart, setDartPosition]
  );

  const computeTrajectory = useCallback(
    (vector) => {
      return computeTrajectoryPoints(vector, anchorRef.current, floorY, bounds.width);
    },
    [bounds.width, floorY]
  );

  const releaseShot = useCallback(
    (point) => {
      setIsDragging(false);
      setAimVector(null);
      setTrajectoryPoints([]);
      if (!anchorRef.current) return;

      const dragVector = {
        x: point.x - anchorRef.current.x,
        y: point.y - anchorRef.current.y,
      };

      const distance = Math.hypot(dragVector.x, dragVector.y);
      if (distance < DART_GAME.MIN_DRAG_DISTANCE) return;

      velocityRef.current = calculateVelocity(dragVector);

      shotActiveRef.current = true;
      setShotActive(true);
      lastTimeRef.current = null;
      flightStartRef.current = null;
      animationRef.current = requestAnimationFrame(step);
    },
    [step]
  );

  const handlePointerDown = useCallback((event) => {
    if (shotActiveRef.current) return;
    event.preventDefault();
    anchorRef.current = { ...posRef.current };
    setAimVector({ x: 0, y: 0 });
    setTrajectoryPoints([]);
    setIsDragging(true);
  }, []);

  useEffect(() => {
    const updateBounds = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setBounds({ width: rect.width, height: rect.height });
      setDartPosition({
        x: rect.width / 2,
        y: Math.max(DART_GAME.DART_SIZE + 8, rect.height - DART_GAME.DART_SIZE - 30),
      });
    };

    updateBounds();
    window.addEventListener("resize", updateBounds);
    return () => window.removeEventListener("resize", updateBounds);
  }, [setDartPosition]);

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (event) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const point = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      const dragVector = {
        x: point.x - anchorRef.current.x,
        y: point.y - anchorRef.current.y,
      };
      setAimVector(dragVector);
      setTrajectoryPoints(computeTrajectory(dragVector));
    };

    const handlePointerUp = (event) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const point = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      releaseShot(point);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [computeTrajectory, isDragging, releaseShot]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, []);

  const aimDistance = useMemo(
    () => aimVector ? Math.min(Math.hypot(aimVector.x, aimVector.y), DART_GAME.MAX_PULL) : 0,
    [aimVector]
  );
  
  const aimAngle = useMemo(
    () => aimVector ? Math.atan2(aimVector.y, aimVector.x) : 0,
    [aimVector]
  );

  return (
    <div className="game-layer">
      <div className="dart-game" ref={containerRef}>
        <div className="game-targets">
          {NAV_TARGETS.map((target, index) => (
            <Target
              key={target.id}
              target={target}
              isHit={hitTarget === target.id}
              targetRef={(node) => {
                targetRefs.current[index] = node;
              }}
            />
          ))}
        </div>

        {isDragging && aimVector && (
          <div
            className="aim-line"
            style={{
              width: `${aimDistance}px`,
              transform: `translate(${anchorRef.current.x}px, ${anchorRef.current.y}px) rotate(${aimAngle}rad)`,
            }}
          />
        )}

        {trajectoryPoints.map((point, index) => (
          <span
            key={`traj-${index}`}
            className="trajectory-dot"
            style={{
              left: `${point.x - 4}px`,
              top: `${point.y - 4}px`,
              opacity: point.opacity,
            }}
          />
        ))}

        {bounds.width > 0 && bounds.height > 0 && (
          <Dart
            position={dartPos}
            rotation={dartRotation}
            isDragging={isDragging}
            isInFlight={shotActive}
            onPointerDown={handlePointerDown}
          />
        )}

        <ToastNotifications toasts={toasts} />
      </div>
    </div>
  );
}
